import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { Form } from '../models';
import {
  FormioComponentKeyRef,
  FormioComponentType,
  KeyRefType,
  LookupType,
  TransactionType,
} from '../enums';

export type LookupFilterEntities = {
  [key in 'PATIENT' | 'DOCTOR' | 'FACILITY']: number[];
};

export interface LookupMappingKeys {
  url: string;
  filter: string;
  template: string;
}

export interface SchemaConfigs {
  companyId: number;
  lookupType: LookupType;
  label: string;
  key: string;
  dataValues: { label: string; value: string }[];
  linkedTo: string;
}

@Injectable()
export class FormUtility {
  private _prefixes = [];
  private _flattenedSchemaComponents = [];

  private static getLookupMappingKeys(
    companyId: number,
    lookupType: LookupType,
    key: string,
  ): LookupMappingKeys {
    switch (lookupType) {
      case LookupType.PATIENT:
        return {
          url: `${environment.CORE_BASE}/companies/${companyId}/patients`,
          filter:
            'FILTER&size=25&sort=firstName,asc&sort=middleName,asc&sort=lastName,asc&sort=lastName2,asc',
          template:
            '<span>{{ item.first_name }} {{ item.middle_name }} {{ item.last_name }} {{ item.last_name2 }}</span>',
        };

      case LookupType.DOCTOR:
        return {
          url: `${environment.CORE_BASE}/companies/${companyId}/doctors`,
          filter:
            'FILTER&size=25&sort=firstName,asc&sort=middleName,asc&sort=lastName,asc&sort=lastName2,asc',
          template:
            '<span>{{ item.first_name }} {{ item.middle_name }} {{ item.last_name }} {{ item.last_name2 }}</span>',
        };

      case LookupType.FACILITY:
        return {
          url: `${environment.CORE_BASE}/companies/${companyId}/facilities`,
          filter: 'FILTER&size=25&sort=lastName,asc',
          template: '<span>{{ item.name }}</span>',
        };

      case LookupType.CONTACT:
        return {
          url: `${environment.CORE_BASE}/companies/${companyId}/contacts`,
          filter:
            'FILTER&size=25&sort=firstName,asc&sort=middleName,asc&sort=lastName,asc&sort=lastName2,asc',
          template:
            '<span>{{ item.first_name }} {{ item.middle_name }} {{ item.last_name }} {{ item.last_name2 }}</span>',
        };

      case LookupType.PRESCRIPTION:
        return {
          url: `${environment.CORE_BASE}/companies/${companyId}/patients/{{ data.${key}Patient.id }}/prescriptions`,
          filter: 'FILTER&is_active=true&size=25&sort=drug_name,asc',
          template:
            '<span>RX: {{ item.pms_rx_number }} {{ item.drug.drug }} {{ item.drug.strength }}{{ item.drug.unit }} filled:{{ item.last_filled_date }}</span>',
        };

      case LookupType.DRUG:
        return {
          url: `${environment.CORE_BASE}/companies/${companyId}/drugs`,
          filter: 'FILTER&size=25&sort=drug,asc',
          template: '<span>{{ item.drug }}</span>',
        };

      case LookupType.PHARMACY:
        return {
          url: `${environment.CORE_BASE}/companies/${companyId}/pharmacies`,
          filter: 'FILTER&size=25&sort=lastName,asc',
          template: '<span>{{ item.name }}</span>',
        };

      case LookupType.INSURANCE:
        return {
          url: `${environment.CORE_BASE}/companies/${companyId}/insurance`,
          filter: 'FILTER&size=25&sort=name,asc',
          template: '<span>{{ item.name }}</span>',
        };

      case LookupType.CLIENT:
        return {
          url: `${environment.CORE_BASE}/companies/${companyId}/clients`,
          filter: 'FILTER&size=25&sort=lastName,asc',
          template:
            '<span>{{ item.first_name }} {{ item.middle_name }} {{ item.last_name }} {{ item.last_name2 }}</span>',
        };

      case LookupType.OFFICE:
        return {
          url: `${environment.USER_BASE}/all-office`,
          filter: 'FILTER&size=25',
          template: '<span>{{ item.name }}</span>',
        };
    }
  }

  private static getLookupFilter(
    transactionType: TransactionType,
    lookupType: LookupType,
    filterEntities: LookupFilterEntities,
  ): string {
    let filter = '';

    if (transactionType === TransactionType.TASK) {
      switch (lookupType) {
        case LookupType.PATIENT:
          if (filterEntities[LookupType.PATIENT]?.length) {
            filterEntities[LookupType.PATIENT].map((id) => (filter += `&id=${id}`));
          } else {
            filterEntities[LookupType.DOCTOR]?.map((id) => (filter += `&doctor_id=${id}`));
            filterEntities[LookupType.FACILITY]?.map((id) => (filter += `&facility_id=${id}`));
          }
          filter += `&is_active=true`;
          break;

        case LookupType.DOCTOR:
          filterEntities[LookupType.DOCTOR]?.map((id) => (filter += `&id=${id}`));
          break;

        case LookupType.FACILITY:
          filterEntities[LookupType.FACILITY]?.map((id) => (filter += `&id=${id}`));
          break;

        case LookupType.CONTACT:
          Object.keys(filterEntities).forEach((key) =>
            filterEntities[key]?.forEach((id) => (filter += `&related_id=${id}`)),
          );
          break;
      }
    }

    return filter;
  }

  private static getKeyPrefix(schema): string {
    switch (FormioComponentKeyRef[schema?.type] || KeyRefType.DEFAULT) {
      case KeyRefType.KEY:
        return schema.key;

      case KeyRefType.TYPE:
        return schema.type;

      case KeyRefType.VALUE:
        return schema.key;

      default:
        return '';
    }
  }

  private static getChildComponentKey(parentComponent, childComponent): string {
    switch (FormioComponentKeyRef[parentComponent?.type] || KeyRefType.DEFAULT) {
      case KeyRefType.KEY:
        return childComponent?.key;

      case KeyRefType.TYPE:
        return childComponent?.key;

      case KeyRefType.VALUE:
        return childComponent?.value;

      default:
        return childComponent?.key;
    }
  }

  getSchema(schemaConfigs: SchemaConfigs) {
    const { companyId, lookupType, label, key, dataValues, linkedTo } = schemaConfigs;

    if (!lookupType)
      return {
        label,
        placeholder: label,
        widget: 'choicesjs',
        tableView: true,
        data: {
          values: dataValues,
        },
        selectThreshold: 0.3,
        key,
        type: 'select',
        indexeddb: {
          filter: {},
        },
        input: true,
      };

    const { url, filter, template } = FormUtility.getLookupMappingKeys(companyId, lookupType, key);

    return {
      lookup: lookupType,
      label,
      placeholder: label,
      widget: 'choicesjs',
      tableView: true,
      dataSrc: 'url',
      data: {
        url,
        headers: [
          {
            key: 'Authorization',
            value: `Bearer TOKEN`,
          },
        ],
      },
      template,
      selectThreshold: 0.4,
      validate: {
        select: false,
      },
      key,
      linkedTo,
      type: 'select',
      indexeddb: {
        filter: {},
      },
      searchField: lookupType === LookupType.OFFICE ? 'term' : 'search_term',
      minSearch: 1,
      filter,
      input: true,
      selectValues: 'data',
      disableLimit: false,
    };
  }

  private walkSchemaComponents(schema, visit: (component) => void): void {
    if (!schema) return;

    if (Array.isArray(schema)) {
      schema.forEach((item) => this.walkSchemaComponents(item, visit));
      return;
    }

    if (schema.type === 'select') {
      visit(schema);
    }

    const nested = schema[FormioComponentType[schema?.type] || FormioComponentType.default];
    if (Array.isArray(nested) && nested.length) {
      nested.forEach((child) => this.walkSchemaComponents(child, visit));
    }
  }

  applyRuntimePlaceholders(schema, token: string) {
    const clone = JSON.parse(JSON.stringify(schema || { components: [] }));

    this.walkSchemaComponents(clone, (component) => {
      const headerValue = component.data?.headers?.[0]?.value;
      if (typeof headerValue === 'string' && headerValue.includes('TOKEN')) {
        component.data.headers[0].value = headerValue.replace(/TOKEN/g, token || '');
      }

      if (typeof component.filter === 'string' && component.filter.includes('FILTER')) {
        component.filter = component.filter.replace('FILTER', '');
      }
    });

    return clone;
  }

  revertRuntimePlaceholders(schema, token: string) {
    const clone = JSON.parse(JSON.stringify(schema || { components: [] }));

    this.walkSchemaComponents(clone, (component) => {
      const headerValue = component.data?.headers?.[0]?.value;
      if (typeof headerValue === 'string') {
        if (token && headerValue.includes(token)) {
          component.data.headers[0].value = headerValue.replace(token, 'TOKEN');
        } else if (/^Bearer\s+\S+/i.test(headerValue) && !headerValue.includes('TOKEN')) {
          component.data.headers[0].value = 'Bearer TOKEN';
        }
      }

      if (
        typeof component.filter === 'string' &&
        component.data?.url &&
        !component.filter.includes('FILTER')
      ) {
        component.filter = `FILTER${component.filter.startsWith('&') ? '' : '&'}${
          component.filter
        }`;
      }
    });

    return clone;
  }

  private generateSchemaKeysValue(
    schema,
    synonymValueMap,
    token: string,
    transactionType: TransactionType,
    filterEntities: LookupFilterEntities,
  ): void {
    let childComponents = schema[FormioComponentType[schema?.type] || FormioComponentType.default];
    const prefix = FormUtility.getKeyPrefix(schema);

    this._prefixes.push(prefix);

    childComponents.forEach((c) => {
      childComponents = c[FormioComponentType[c?.type] || FormioComponentType.default];

      if (childComponents)
        this.generateSchemaKeysValue(c, synonymValueMap, token, transactionType, filterEntities);
      else if (c.type === 'select') {
        const filteredPrefixes = this._prefixes.filter((p) => !!p);
        const key = filteredPrefixes.length
          ? `${filteredPrefixes.join('.')}.${FormUtility.getChildComponentKey(schema, c)}`
          : c.key;

        if (c.lookup) {
          c.data.headers[0].value = c.data.headers[0].value.replace('TOKEN', token);
          c.filter = c.filter.replace(
            'FILTER',
            FormUtility.getLookupFilter(transactionType, c.lookup, filterEntities),
          );
        } else if (!!synonymValueMap && !!synonymValueMap[key]?.length) {
          c.data = { values: synonymValueMap[key] };
        }
      }
    });

    if (this._prefixes.length) this._prefixes.pop();
  }

  getSchemaKeysValue(
    form: Form,
    token: string,
    transactionType: TransactionType,
    filterEntities: LookupFilterEntities,
  ): string {
    const schema = JSON.parse(form.schema || '{ components: [] }');

    this.generateSchemaKeysValue(
      schema,
      form.field_synonym_value_map,
      token,
      transactionType,
      filterEntities,
    );
    this._prefixes = [];

    return JSON.stringify(schema);
  }

  private generateFlattenedSchemaComponents(schema): void {
    let childComponents = schema[FormioComponentType[schema?.type] || FormioComponentType.default];
    const prefix = FormUtility.getKeyPrefix(schema);

    this._prefixes.push(prefix);

    childComponents.forEach((c) => {
      childComponents = c[FormioComponentType[c?.type] || FormioComponentType.default];

      if (childComponents) this.generateFlattenedSchemaComponents(c);
      else {
        const newChild = { ...c };
        const filteredPrefixes = this._prefixes.filter((p) => !!p);

        newChild.key = filteredPrefixes.length
          ? `${filteredPrefixes.join('.')}.${FormUtility.getChildComponentKey(schema, newChild)}`
          : newChild.key;

        this._flattenedSchemaComponents.push(newChild);
      }
    });

    if (this._prefixes.length) this._prefixes.pop();
  }

  getFlattenedSchemaComponents(schema): any[] {
    this.generateFlattenedSchemaComponents(schema);

    const flattenedSchemaComponents = this._flattenedSchemaComponents;

    this._prefixes = [];
    this._flattenedSchemaComponents = [];

    return flattenedSchemaComponents;
  }

  private generateUpdatedSchema(companyId: number, schema, updatedComponents): void {
    const childComponents =
      schema[FormioComponentType[schema?.type] || FormioComponentType.default];
    const prefix = FormUtility.getKeyPrefix(schema);

    this._prefixes.push(prefix);

    childComponents?.forEach((c, idx) => {
      if (c[FormioComponentType[c?.type] || FormioComponentType.default])
        this.generateUpdatedSchema(companyId, c, updatedComponents);
      else {
        const filteredPrefixes = this._prefixes.filter((p) => !!p);
        const index = updatedComponents?.findIndex((u) => {
          return (
            u.key ===
            (filteredPrefixes.length
              ? `${filteredPrefixes.join('.')}.${FormUtility.getChildComponentKey(schema, c)}`
              : c.key)
          );
        });
        const updatedComponent = updatedComponents[index];

        if (updatedComponent) {
          updatedComponent.key = FormUtility.getChildComponentKey(schema, c);

          const prevComponent = { ...c };

          childComponents[idx] = updatedComponent;

          if (updatedComponent.lookup === LookupType.PRESCRIPTION) {
            if (prevComponent.lookup !== LookupType.PRESCRIPTION)
              childComponents.splice(
                idx,
                0,
                this.getSchema({
                  companyId,
                  lookupType: LookupType.PATIENT,
                  label: `${updatedComponent.label} Patient`,
                  key: `${updatedComponent.key}Patient`,
                  dataValues: [],
                  linkedTo: updatedComponent.key,
                }),
              );
          } else if (c.lookup === LookupType.PRESCRIPTION && !!childComponents[idx - 1]) {
            childComponents.splice(idx - 1, 1);
          }

          updatedComponents.splice(index, 1);
        }
      }
    });

    if (this._prefixes.length) this._prefixes.pop();
  }

  updateSchema(companyId: number, schema, updatedComponents) {
    if (!schema || !updatedComponents?.length) return;

    const newSchema = JSON.parse(JSON.stringify(schema));
    const newUpdatedComponents = JSON.parse(JSON.stringify(updatedComponents));

    this.generateUpdatedSchema(companyId, newSchema, newUpdatedComponents);
    this._prefixes = [];

    return newSchema;
  }
}
